import type { CloudBudget } from './api-client';

const BUDGET_CACHE_PREFIX = 'budget.cloud.budget.v1:';

type ReadStorageLike = Pick<Storage, 'getItem'>;
type RemoveStorageLike = Pick<Storage, 'removeItem'>;
type WriteStorageLike = Pick<Storage, 'setItem'>;

export function readCloudBudgetId(
  subject: string,
  workspaceId: string,
  localBudgetId: string,
  storage: ReadStorageLike = localStorage,
): string | null {
  return storage.getItem(cacheKey(subject, workspaceId, localBudgetId));
}

export function loadCloudBudgetId(
  subject: string,
  workspaceId: string,
  localBudgetId: string,
  budgets: readonly CloudBudget[],
  storage: ReadStorageLike = localStorage,
): string | null {
  const cached = readCloudBudgetId(
    subject,
    workspaceId,
    localBudgetId,
    storage,
  );
  return selectActiveBudget(budgets, cached);
}

export function saveCloudBudgetId(
  subject: string,
  workspaceId: string,
  localBudgetId: string,
  cloudBudgetId: string,
  storage: WriteStorageLike = localStorage,
): void {
  storage.setItem(cacheKey(subject, workspaceId, localBudgetId), cloudBudgetId);
}

export function clearCloudBudgetId(
  subject: string,
  workspaceId: string,
  localBudgetId: string,
  storage: RemoveStorageLike = localStorage,
): void {
  storage.removeItem(cacheKey(subject, workspaceId, localBudgetId));
}

export function selectActiveBudget(
  budgets: readonly CloudBudget[],
  preferredId: string | null,
): string | null {
  const active = budgets.filter(budget => budget.status === 'active');
  if (
    preferredId !== null &&
    active.some(budget => budget.id === preferredId)
  ) {
    return preferredId;
  }
  return active.length === 1 ? (active[0]?.id ?? null) : null;
}

function cacheKey(
  subject: string,
  workspaceId: string,
  localBudgetId: string,
): string {
  return [subject, workspaceId, localBudgetId]
    .map(value => encodeURIComponent(value))
    .join(':')
    .replace(/^/, BUDGET_CACHE_PREFIX);
}
